import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Coach-facing modal — invite N parents via SMS in one shot.
 *
 * The textarea accepts any reasonable mix: one phone per line, commas,
 * spaces — we split on non-digit/+ and de-dupe before posting.
 * `note` is optional; we cap at 280 chars to leave room for the link.
 */
export default function InviteParents({ onClose, theme = 'dark' }) {
  const { token } = useAuth();
  const [text, setText] = useState('');
  const [note, setNote] = useState('Hey — Cricket Refreshed gives your kid an AI coach + video feedback. Free to try.');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const phones = parsePhones(text);

  const send = async () => {
    if (!phones.length) {
      toast.error('Add at least one phone number');
      return;
    }
    setBusy(true);
    setResults(null);
    try {
      const r = await axios.post(`${API}/coach/roster/invite`, {
        phone_numbers: phones,
        note: note.trim() || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(r.data?.results || []);
      const delivered = (r.data?.results || []).filter((x) => x.delivered).length;
      toast.success(`Sent ${delivered} of ${phones.length} invitations`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send invites';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl p-5 max-w-md w-full"
        style={{
          background: '#0a0e27', color: '#f7fafc',
          border: '1px solid rgba(0,201,167,0.3)',
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#00C9A7' }}>
              SMS roster invite
            </div>
            <h2 className="text-lg font-bold mt-0.5">Invite parents</h2>
          </div>
          <button onClick={onClose} className="text-xl opacity-60 hover:opacity-100">✕</button>
        </div>
        <p className="text-xs opacity-70 mb-4">
          Each parent gets a text with a one-tap link. Once they sign up,
          they're automatically added to your roster.
        </p>

        <label className="block text-xs uppercase tracking-widest font-bold opacity-70 mb-1.5">
          Phone numbers
        </label>
        <textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"+1 415 555 0100\n+1 408 555 0142\n+1 650 555 0117"}
          className="w-full px-3 py-2 rounded-lg outline-none text-sm font-mono"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        />
        <div className="text-[11px] opacity-60 mt-1">
          {phones.length === 0
            ? 'Tip: one per line, or paste a comma-separated list.'
            : `${phones.length} number${phones.length === 1 ? '' : 's'} ready to invite.`}
        </div>

        <label className="block text-xs uppercase tracking-widest font-bold opacity-70 mb-1.5 mt-4">
          Note (optional, 280 char max)
        </label>
        <textarea
          rows={3} maxLength={280}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full px-3 py-2 rounded-lg outline-none text-sm"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        />

        <button
          onClick={send}
          disabled={busy || !phones.length}
          className="w-full mt-4 py-2.5 rounded-lg font-semibold text-white"
          style={{
            background: busy || !phones.length
              ? 'rgba(150,160,180,0.3)'
              : 'linear-gradient(135deg, #00A651, #00D9FF)',
          }}
        >
          {busy ? 'Sending…' : `Send ${phones.length || ''} invite${phones.length === 1 ? '' : 's'}`}
        </button>

        {results && (
          <div className="mt-4 max-h-48 overflow-y-auto space-y-1.5 text-xs">
            {results.map((r) => (
              <div
                key={r.phone}
                className="flex items-center justify-between px-2.5 py-1.5 rounded"
                style={{
                  background: r.delivered ? 'rgba(0,201,167,0.10)'
                              : r.deduped  ? 'rgba(96,165,250,0.10)'
                              :              'rgba(239,68,68,0.10)',
                }}
              >
                <span>{r.phone}</span>
                <span style={{
                  color: r.delivered ? '#00C9A7'
                          : r.deduped ? '#60a5fa'
                          : '#ef4444',
                  fontWeight: 700,
                }}>
                  {r.delivered ? 'sent'
                    : r.deduped ? 'already pending'
                    : r.error || 'failed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function parsePhones(text) {
  if (!text) return [];
  // Split on anything that isn't part of a phone number, then collapse.
  const tokens = text.split(/[^\d+]/).filter((t) => t.replace(/[^\d]/g, '').length >= 7);
  // De-dupe by digit-only form.
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    const k = t.replace(/[^\d]/g, '');
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t.startsWith('+') ? t : `+${k}`);
  }
  return out;
}
